import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import { LoadingLocalTakeover } from 'web-ui-components/lib/organisms/takeovers';

import { connect } from 'react-redux';
import accountPropTypes, { fromAccountPropType } from '../../../states/accounts/propTypes';
import AccountPicker from '../AccountPicker/AccountPicker';
import PayeePicker from '../PayeePicker/PayeePicker';

import { retryLastApiAction, getPage, setCurrentPage, setSearchText } from '../../../states/accountPaging/actions';
import { getPagingLayoutType, getSearchText } from '../../../states/accountPaging/selectors';
import { getSelectedFromAccount } from '../../../states/payeeSelector/selectors';
import { FromToCurrencySection } from '../../../ift/components/IFTPaymentDetails/FromToCurrencySection/FromToCurrencySection';
import { ACCOUNT_PICKER_LAYOUT, PAYEE_PICKER_LAYOUT } from '../../constants';
import { contentPropTypes as managePayeeListContent } from '../../../managePayee/components/managePayeeList/managePayeeList.content';
import { getFromAccounts } from '../../../states/accounts/selectors';
import { removeFavouriteAccount, addFavouriteAccount } from '../../../states/favourites/actions';
import { getFavouriteAccountsForPicker } from '../../../states/favourites/selectors';
import { canAddFavourite } from '../../../utils/favouriteUtils';
import TooManyFavouritesErrorModal from './TooManyFavouritesErrorModal';
import { payeePropTypes } from '../../../sharedComponents/FromToPayeeSelector/propTypes';

const mapState = (state) => ({
	fromAccount: getSelectedFromAccount(state),
	fromAccounts: getFromAccounts(state),
	layoutType: getPagingLayoutType(state),
	favourites: getFavouriteAccountsForPicker(state),
	searchText: getSearchText(state),
});

const mapDispatch = (dispatch) => {
	const debouncedGetPage = debounce((page) => dispatch(getPage(page)), 500);
	return {
		onRetry: () => dispatch(retryLastApiAction()),
		initializeGetPage: (page) => dispatch(getPage(page)),
		onPageChanged: (page) => {
			dispatch(setCurrentPage(page));
			if (typeof page === 'number' && page > 0) {
				debouncedGetPage(page);
			} else debouncedGetPage.cancel();
		},
		onSearchTextChanged: (text) => dispatch(setSearchText(text)),
		onSearchTextSubmit: () => dispatch(getPage(1)),
		removeFavouriteAccount: (accountId) => dispatch(removeFavouriteAccount(accountId)),
		addFavouriteAccount: (account) => dispatch(addFavouriteAccount(account)),
	};
};

class LayoutContainer extends Component {
	constructor(props) {
		super(props);
		this.state = { showTooManyFavouritesModal: false };
		this.handleFavouriteIconClick = this.handleFavouriteIconClick.bind(this);
		this.renderPicker = this.renderPicker.bind(this);
		this.handleSearchTextSubmit = this.handleSearchTextSubmit.bind(this);
	}
	handleFavouriteIconClick(account) {
		const { favourites, addFavouriteAccount, removeFavouriteAccount } = this.props;

		if (favourites.some((favouriteAccount) => favouriteAccount.id === account.id)) {
			return removeFavouriteAccount(account.id);
		}
		if (canAddFavourite(favourites)) {
			return addFavouriteAccount(account);
		}
		return this.setState({ showTooManyFavouritesModal: true });
	}
	handleSearchTextSubmit() {
		const { onSearchTextSubmit, searchText, onSearchTextChanged } = this.props;
		const trimmedSearchText = searchText.trim();
		onSearchTextChanged(trimmedSearchText);
		if (trimmedSearchText) {
			onSearchTextSubmit();
		}
	}

	renderPicker() {
		const {
			onPageChanged,
			onSelectAccount,
			onRetry,
			onSearchTextChanged,
			layoutType,
			handleDisplayFullwidth,
			handleGoBack,
			isManageYourPayee,
			handlePayeeSelect,
			getIsPayeeSelected,
			handleViewPayee,
			handleDeletePayee,
			handleEditPayee,
			handleEditChaps,
			handleImportPayees,
			isFileImportAllowed,
			handleTransferPayee,
			fileUploadContent,
			isBootStrapInProgress,
			initializeGetPage,
			selectedPayees,
			isPaymentFlow,
		} = this.props;
		if (layoutType === ACCOUNT_PICKER_LAYOUT) {
			return (
				<AccountPicker
					onPageChanged={onPageChanged}
					onSelectAccount={onSelectAccount}
					onSearchTextChanged={onSearchTextChanged}
					onRetry={onRetry}
					handleGoBack={handleGoBack}
					onSearchTextSubmit={this.handleSearchTextSubmit}
					handleFavouriteIconClick={this.handleFavouriteIconClick}
					isBootStrapInProgress={isBootStrapInProgress}
					initializeGetPage={initializeGetPage}
				/>
			);
		}
		if (layoutType === PAYEE_PICKER_LAYOUT) {
			return (
				<PayeePicker
					onPageChanged={onPageChanged}
					handleDisplayFullwidth={handleDisplayFullwidth}
					handleGoBack={handleGoBack}
					handlePayeeSelect={handlePayeeSelect}
					onSearchTextChanged={onSearchTextChanged}
					onSearchTextSubmit={this.handleSearchTextSubmit}
					onRetry={onRetry}
					getIsPayeeSelected={getIsPayeeSelected}
					isManageYourPayee={isManageYourPayee}
					handleViewPayee={handleViewPayee}
					handleDeletePayee={handleDeletePayee}
					handleEditPayee={handleEditPayee}
					handleEditChaps={handleEditChaps}
					isFileImportAllowed={isFileImportAllowed}
					handleImportPayees={handleImportPayees}
					handleTransferPayee={handleTransferPayee}
					fileUploadContent={fileUploadContent}
					handleFavouriteIconClick={this.handleFavouriteIconClick}
					isBootStrapInProgress={isBootStrapInProgress}
					initializeGetPage={initializeGetPage}
					selectedPayees={selectedPayees}
					isPaymentFlow={isPaymentFlow}
				/>
			);
		}
		return null;
	}
	render() {
		const { fromAccount, handleGoBack, paymentMode, fromAccounts, isPaymentFlow, isBootStrapInProgress } = this.props;
		const { showTooManyFavouritesModal } = this.state;
		return (
			<LoadingLocalTakeover show={isBootStrapInProgress} title="Loading">
				<FromToCurrencySection
					fromAccount={fromAccount}
					toAccount=""
					fromAccounts={fromAccounts}
					toPayees={[]}
					showAmountField={false}
					handleClose={handleGoBack}
					paymentMode={paymentMode}
					isPaymentFlow={isPaymentFlow}
				/>
				{this.renderPicker()}
				<TooManyFavouritesErrorModal
					showTooManyFavouritesModal={showTooManyFavouritesModal}
					onClose={() => this.setState({ showTooManyFavouritesModal: false })}
				/>
			</LoadingLocalTakeover>
		);
	}
}

LayoutContainer.propTypes = {
	favourites: PropTypes.arrayOf(PropTypes.shape(accountPropTypes)),
	onSelectAccount: PropTypes.func.isRequired,
	onRetry: PropTypes.func,
	onPageChanged: PropTypes.func,
	onSearchTextChanged: PropTypes.func,
	layoutType: PropTypes.string.isRequired,
	fromAccount: PropTypes.string.isRequired,
	paymentMode: PropTypes.string.isRequired,
	handleGoBack: PropTypes.func.isRequired,
	handlePayeeSelect: PropTypes.func,
	isManageYourPayee: PropTypes.bool,
	getIsPayeeSelected: PropTypes.func.isRequired,
	handleViewPayee: PropTypes.func,
	handleDeletePayee: PropTypes.func,
	handleDisplayFullwidth: PropTypes.func,
	handleEditPayee: PropTypes.func,
	handleImportPayees: PropTypes.func,
	isFileImportAllowed: PropTypes.bool,
	isPaymentFlow: PropTypes.bool,
	handleTransferPayee: PropTypes.func,
	fileUploadContent: PropTypes.shape({ managePayeeListContent }),
	fromAccounts: PropTypes.arrayOf(PropTypes.shape(fromAccountPropType)).isRequired,
	onSearchTextSubmit: PropTypes.func.isRequired,
	removeFavouriteAccount: PropTypes.func.isRequired,
	addFavouriteAccount: PropTypes.func.isRequired,
	isBootStrapInProgress: PropTypes.bool,
	initializeGetPage: PropTypes.func.isRequired,
	searchText: PropTypes.string.isRequired,
	selectedPayees: PropTypes.arrayOf(PropTypes.shape(PropTypes.shape(payeePropTypes))),
};
export { LayoutContainer };
export default connect(mapState, mapDispatch)(LayoutContainer);
